import { useCallback, useRef, useState } from 'react';
import { useCountdownNow } from './useCountdownNow';
import {
  passwordResetFailureOf,
  usePasswordResetConfirm,
  usePasswordResetRequest,
  usePasswordResetVerify,
} from './usePasswordReset';

export type ProfileResetStep = 'send' | 'code' | 'password' | 'done';

/** An i18n key plus its variables; the view translates it, so locale switches apply to a shown error. */
export interface ResetNotice {
  key: string;
  vars?: Record<string, string | number>;
  /** True when re-running the same step can help (connectivity / server hiccup). */
  retryable?: boolean;
}

/**
 * Profile "reset password" flow over E30–E32 for the signed-in member's own phone. Everything (resetId,
 * resetToken, password) lives in this hook's state and the mutation call only — never storage, never logged,
 * never cached. `reset()` drops it all (called on close and on success).
 */
export function useProfilePasswordReset(phone: string) {
  const request = usePasswordResetRequest();
  const verify = usePasswordResetVerify();
  const confirm = usePasswordResetConfirm();
  const [now, setNow] = useCountdownNow();

  const [step, setStep] = useState<ProfileResetStep>('send');
  const [notice, setNotice] = useState<ResetNotice | null>(null);
  const [resendAt, setResendAt] = useState(0);
  const [blockedUntil, setBlockedUntil] = useState(0); // 429 / lockout deadline (ms epoch)
  const [lockedOut, setLockedOut] = useState(false);
  const resetId = useRef('');
  const resetToken = useRef('');
  const inFlight = useRef(false); // double-submit guard that holds even before React re-renders

  const resendIn = Math.max(0, Math.ceil((resendAt - now) / 1000));
  const blockedIn = Math.max(0, Math.ceil((blockedUntil - now) / 1000));
  const pending = request.isPending || verify.isPending || confirm.isPending;

  const block = (seconds: number) => {
    const at = Date.now();
    setNow(at);
    setBlockedUntil(at + seconds * 1000);
  };

  /** Runs one mutation at a time; extra calls while one is in flight are ignored. */
  const guarded = useCallback((run: (done: () => void) => void) => {
    if (inFlight.current) return;
    inFlight.current = true;
    run(() => {
      inFlight.current = false;
    });
  }, []);

  const failure = (e: unknown, fallback: ResetNotice): ResetNotice => {
    const f = passwordResetFailureOf(e);
    if (f.code === 'RATE_LIMITED') {
      const seconds = f.retryAfterSeconds ?? 60;
      block(seconds);
      return { key: 'profile.password.error.rateWait', vars: { seconds } };
    }
    if (f.code === 'FEATURE_DISABLED') return { key: 'profile.password.error.featureOff' };
    if (f.status === undefined && f.code === undefined) return { key: 'profile.password.error.network', retryable: true };
    if (f.status !== undefined && f.status >= 500) return { ...fallback, retryable: true };
    return fallback;
  };

  const sendCode = useCallback(() => {
    guarded((done) => {
      setNotice(null);
      request.mutate(
        { phone },
        {
          onSuccess: (r) => {
            resetId.current = r.resetId;
            const at = Date.now();
            setNow(at);
            setResendAt(at + r.resendAfterSeconds * 1000);
            setLockedOut(false);
            setStep('code');
          },
          onError: (e) => {
            setNotice(failure(e, { key: 'auth.forgot.error.generic' }));
          },
          onSettled: done,
        },
      );
    });
  }, [phone, guarded, request]);

  const submitCode = useCallback(
    (code: string) => {
      guarded((done) => {
        setNotice(null);
        verify.mutate(
          { resetId: resetId.current, code },
          {
            onSuccess: (r) => {
              resetToken.current = r.resetToken;
              setStep('password');
            },
            onError: (e) => {
              const f = passwordResetFailureOf(e);
              if (f.code === 'OTP_INVALID') {
                setNotice(
                  f.attemptsRemaining !== undefined && f.attemptsRemaining > 0
                    ? { key: 'auth.forgot.error.otpInvalidLeft', vars: { count: f.attemptsRemaining } }
                    : { key: 'auth.forgot.error.otpInvalid' },
                );
              } else if (f.code === 'OTP_EXPIRED') {
                setResendAt(0); // let the member ask for a new code straight away
                setNotice({ key: 'auth.forgot.error.otpExpired' });
              } else if (f.code === 'RESET_LOCKED') {
                const seconds = f.retryAfterSeconds ?? 1800;
                block(seconds);
                setLockedOut(true);
                setNotice({ key: 'auth.forgot.error.locked', vars: { minutes: Math.max(1, Math.ceil(seconds / 60)) } });
              } else {
                setNotice(failure(e, { key: 'auth.forgot.error.generic' }));
              }
            },
            onSettled: done,
          },
        );
      });
    },
    [guarded, verify],
  );

  const reset = useCallback(() => {
    resetId.current = '';
    resetToken.current = '';
    setStep('send');
    setNotice(null);
    setResendAt(0);
    setBlockedUntil(0);
    setLockedOut(false);
  }, []);

  const submitPassword = useCallback(
    (newPassword: string) => {
      guarded((done) => {
        setNotice(null);
        confirm.mutate(
          { resetToken: resetToken.current, newPassword },
          {
            onSuccess: () => {
              resetToken.current = '';
              resetId.current = '';
              setStep('done');
            },
            onError: (e) => {
              const f = passwordResetFailureOf(e);
              if (f.code === 'RESET_TOKEN_INVALID') {
                // Expired or consumed: the only way forward is a fresh code.
                reset();
                setNotice({ key: 'auth.forgot.error.tokenInvalid' });
              } else if (f.code === 'PASSWORD_POLICY') {
                setNotice({ key: 'auth.forgot.error.policy' }); // typed values stay in the form
              } else {
                setNotice(failure(e, { key: 'auth.forgot.error.generic' }));
              }
            },
            onSettled: done,
          },
        );
      });
    },
    [guarded, confirm, reset],
  );

  return {
    step,
    notice,
    pending,
    resendIn,
    /** Seconds until a 429 / lockout clears; 0 when not blocked. */
    blockedIn,
    lockedOut: lockedOut && blockedIn > 0,
    sendCode,
    submitCode,
    submitPassword,
    reset,
  };
}
